import React from 'react';
import { Link, useParams, Navigate } from 'react-router-dom';
import { ArrowLeft, Calendar, Clock, CheckCircle, Users, Star, ArrowRight } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';

const LOGO_URL = 'https://customer-assets.emergentagent.com/job_hoop-tracker-7/artifacts/tisywt9m_attendance%20tracker%20logo.png';

const articles = {
  'free-athlete-attendance-tracking-app': {
    title: 'Free Athlete Attendance Tracking App for Coaches in 2025',
    category: 'Guide',
    readTime: '5 min read',
    date: 'March 2025',
    metaDescription: 'Discover the best free athlete attendance tracking app for coaches. Track attendance, manage rosters, and generate reports without paper or spreadsheets.',
    content: `
      <h2>Why Coaches Need a Digital Attendance Tracking Solution</h2>
      <p>Gone are the days of paper sign-in sheets and messy spreadsheets. Modern coaches need efficient, reliable ways to track athlete attendance across practices, games, and training sessions. Whether you're coaching youth basketball, managing a swim club, or running a track program, keeping accurate attendance records is essential.</p>
      
      <h2>The Problem with Traditional Methods</h2>
      <p>Many coaches still rely on:</p>
      <ul>
        <li><strong>Paper sign-in sheets</strong> - Easy to lose, hard to analyze, and time-consuming to manage</li>
        <li><strong>Excel spreadsheets</strong> - Better than paper, but difficult to access on the field and prone to errors</li>
        <li><strong>Memory</strong> - Simply trying to remember who showed up (not recommended!)</li>
      </ul>
      <p>These methods lead to inaccurate records, wasted time, and frustrated coaches.</p>
      
      <h2>What to Look for in a Free Attendance App</h2>
      <p>When searching for a free athlete attendance tracking solution, look for these key features:</p>
      <ul>
        <li><strong>Mobile-friendly design</strong> - Access and update attendance from your phone on the field</li>
        <li><strong>Easy roster management</strong> - Add athletes quickly and organize by team or program</li>
        <li><strong>Quick attendance marking</strong> - Mark present, absent, late, or excused with one tap</li>
        <li><strong>Attendance history</strong> - View past records and identify patterns</li>
        <li><strong>Export capabilities</strong> - Generate reports for parents, administrators, or league requirements</li>
      </ul>
      
      <h2>Introducing Attendance Tracker</h2>
      <p>Attendance Tracker is a free athlete attendance tracking app designed specifically for coaches. With our free plan, you can:</p>
      <ul>
        <li>Manage up to 5 programs</li>
        <li>Track up to 50 athletes</li>
        <li>Access 30 days of attendance history</li>
        <li>Export reports in CSV format</li>
      </ul>
      <p>Need more? Our Pro plan offers unlimited programs, athletes, and full attendance history for just $5/month.</p>
      
      <h2>Getting Started is Easy</h2>
      <p>Setting up your free attendance tracking system takes less than 5 minutes:</p>
      <ol>
        <li>Create your free account</li>
        <li>Add your programs (teams, training groups, etc.)</li>
        <li>Import or manually add your athletes</li>
        <li>Start tracking attendance!</li>
      </ol>
    `
  },
  'how-to-track-sports-team-attendance': {
    title: 'How to Track Sports Team Attendance Effectively',
    category: 'Best Practices',
    readTime: '4 min read',
    date: 'March 2025',
    metaDescription: 'Learn the best practices for tracking sports team attendance. Improve accountability, identify patterns, and keep accurate records.',
    content: `
      <h2>The Importance of Consistent Attendance Tracking</h2>
      <p>Attendance tracking isn't just about knowing who showed up—it's about building accountability, identifying trends, and ensuring your athletes are getting the development they need. Consistent tracking helps coaches:</p>
      <ul>
        <li>Identify athletes who may be struggling with commitment</li>
        <li>Recognize and reward consistent attendees</li>
        <li>Plan appropriate team activities based on expected turnout</li>
        <li>Communicate effectively with parents about their child's participation</li>
      </ul>
      
      <h2>Best Practices for Effective Attendance Tracking</h2>
      
      <h3>1. Track Attendance at Every Session</h3>
      <p>Whether it's practice, a game, or a team meeting, track attendance consistently. Gaps in your records make it difficult to identify patterns and can lead to disputes with parents or athletes.</p>
      
      <h3>2. Use Categories Beyond Present/Absent</h3>
      <p>A simple present/absent system doesn't tell the whole story. Use categories like:</p>
      <ul>
        <li><strong>Present</strong> - Attended and participated fully</li>
        <li><strong>Late</strong> - Arrived after start time</li>
        <li><strong>Excused</strong> - Absent with valid reason (illness, school event, etc.)</li>
        <li><strong>Absent</strong> - No-show without explanation</li>
      </ul>
      
      <h3>3. Take Attendance at the Same Time</h3>
      <p>Establish a routine—take attendance at the start of each session. This creates consistency and ensures you don't forget.</p>
      
      <h3>4. Make It Quick and Easy</h3>
      <p>If attendance tracking is cumbersome, you won't do it consistently. Use a tool that lets you mark attendance in seconds, not minutes.</p>
      
      <h3>5. Review Patterns Regularly</h3>
      <p>Set aside time weekly or monthly to review attendance patterns. Look for:</p>
      <ul>
        <li>Athletes with declining attendance</li>
        <li>Specific days or times with low turnout</li>
        <li>Seasonal patterns (holidays, exam periods, etc.)</li>
      </ul>
      
      <h2>Technology Makes It Easier</h2>
      <p>Modern attendance tracking apps like Attendance Tracker eliminate the hassle of paper records. With features like one-tap marking, automatic reporting, and historical analysis, you can focus on coaching instead of paperwork.</p>
    `
  },
  'best-attendance-tracking-youth-sports': {
    title: 'Best Attendance Tracking Solutions for Youth Sports',
    category: 'Comparison',
    readTime: '6 min read',
    date: 'March 2025',
    metaDescription: 'Compare the best attendance tracking solutions for youth sports programs. Find the right tool for your team, club, or league.',
    content: `
      <h2>Choosing the Right Attendance Tracking System</h2>
      <p>Youth sports programs have unique attendance tracking needs. Parents want visibility, coaches need simplicity, and administrators require reports. Let's compare the most common solutions.</p>
      
      <h2>Paper Sign-In Sheets</h2>
      <p><strong>Pros:</strong></p>
      <ul>
        <li>No technology required</li>
        <li>Familiar to everyone</li>
        <li>Zero cost</li>
      </ul>
      <p><strong>Cons:</strong></p>
      <ul>
        <li>Easy to lose or damage</li>
        <li>Difficult to analyze trends</li>
        <li>No backup if sheets are lost</li>
        <li>Time-consuming to compile reports</li>
      </ul>
      <p><strong>Verdict:</strong> Not recommended for any program serious about attendance tracking.</p>
      
      <h2>Spreadsheets (Excel, Google Sheets)</h2>
      <p><strong>Pros:</strong></p>
      <ul>
        <li>Free to use</li>
        <li>Can create formulas for automatic calculations</li>
        <li>Shareable with assistants</li>
      </ul>
      <p><strong>Cons:</strong></p>
      <ul>
        <li>Difficult to update on mobile devices</li>
        <li>Prone to accidental edits or deletions</li>
        <li>Requires manual setup and maintenance</li>
        <li>Not designed for attendance tracking</li>
      </ul>
      <p><strong>Verdict:</strong> A step up from paper, but still has significant limitations.</p>
      
      <h2>Generic Team Management Apps</h2>
      <p><strong>Pros:</strong></p>
      <ul>
        <li>May include scheduling, communication, and other features</li>
        <li>Often have mobile apps</li>
      </ul>
      <p><strong>Cons:</strong></p>
      <ul>
        <li>Attendance tracking is often an afterthought</li>
        <li>Can be expensive</li>
        <li>Feature bloat—you pay for things you don't need</li>
      </ul>
      <p><strong>Verdict:</strong> Good if you need an all-in-one solution, but may be overkill for attendance-focused needs.</p>
      
      <h2>Dedicated Attendance Tracking Apps</h2>
      <p><strong>Pros:</strong></p>
      <ul>
        <li>Purpose-built for tracking attendance</li>
        <li>Simple, focused interface</li>
        <li>Quick to learn and use</li>
        <li>Often more affordable than full team management suites</li>
      </ul>
      <p><strong>Cons:</strong></p>
      <ul>
        <li>May need separate tools for scheduling or communication</li>
      </ul>
      <p><strong>Verdict:</strong> The best choice for coaches who want simple, effective attendance tracking.</p>
      
      <h2>Why Attendance Tracker Stands Out</h2>
      <p>Attendance Tracker was built specifically for coaches who need reliable attendance tracking without the complexity of full team management suites. Our free plan is perfect for small programs, and Pro users get unlimited tracking capabilities for less than a cup of coffee per month.</p>
    `
  },
  'why-coaches-need-attendance-tracking': {
    title: 'Why Every Coach Needs an Attendance Tracking System',
    category: 'Insights',
    readTime: '4 min read',
    date: 'March 2025',
    metaDescription: 'Discover why attendance tracking is essential for coaches. From accountability to insurance requirements, learn the benefits of systematic tracking.',
    content: `
      <h2>More Than Just Taking Roll</h2>
      <p>Attendance tracking might seem like administrative busywork, but it serves critical functions that every coach should understand. Here's why implementing a proper attendance tracking system is essential for your program.</p>
      
      <h2>1. Building Team Accountability</h2>
      <p>When athletes know their attendance is being tracked, they're more likely to show up consistently. This isn't about punishment—it's about creating a culture of commitment. Athletes who attend regularly improve faster, bond better with teammates, and contribute more to team success.</p>
      
      <h2>2. Identifying At-Risk Athletes</h2>
      <p>Declining attendance often signals deeper issues. An athlete might be:</p>
      <ul>
        <li>Struggling with burnout</li>
        <li>Having conflicts with teammates</li>
        <li>Dealing with family or school problems</li>
        <li>Losing interest in the sport</li>
      </ul>
      <p>By tracking attendance patterns, you can identify and address these issues before they lead to an athlete quitting entirely.</p>
      
      <h2>3. Parent Communication</h2>
      <p>Parents invest time and money in their children's sports activities. They deserve to know whether their child is actually attending. Attendance records help you:</p>
      <ul>
        <li>Provide objective feedback during parent meetings</li>
        <li>Address concerns about playing time fairly (attendance often correlates with skill development)</li>
        <li>Document issues if conflicts arise</li>
      </ul>
      
      <h2>4. Insurance and Liability</h2>
      <p>Many youth sports organizations require attendance records for insurance purposes. If an incident occurs, you may need to prove:</p>
      <ul>
        <li>Who was present at the time</li>
        <li>That proper supervision ratios were maintained</li>
        <li>The athlete's participation history</li>
      </ul>
      <p>Paper records can be lost or disputed. Digital records with timestamps provide reliable documentation.</p>
      
      <h2>5. Program Improvement</h2>
      <p>Attendance data reveals valuable insights about your program:</p>
      <ul>
        <li>Are certain practice times less popular?</li>
        <li>Does attendance drop during specific seasons?</li>
        <li>Are new athletes retained at acceptable rates?</li>
      </ul>
      <p>This information helps you make data-driven decisions about scheduling, programming, and retention strategies.</p>
      
      <h2>6. Recognition and Rewards</h2>
      <p>Athletes with excellent attendance deserve recognition. Whether it's an end-of-season award, extra playing time, or simply acknowledgment, you can't reward what you don't track.</p>
      
      <h2>Getting Started</h2>
      <p>Implementing attendance tracking doesn't have to be complicated. With tools like Attendance Tracker, you can start tracking today—for free. Your future self (and your athletes) will thank you.</p>
    `
  }
};

const CallToAction = () => (
  <Card className="bg-gradient-to-r from-blue-600 to-blue-700 border-0 my-8">
    <CardContent className="p-8 text-center">
      <div className="flex justify-center mb-4">
        <Star className="h-8 w-8 text-yellow-400" />
      </div>
      <h3 className="text-2xl font-bold text-white mb-2">
        Ready to Start Tracking Attendance?
      </h3>
      <p className="text-blue-100 mb-6">
        Join thousands of coaches using Attendance Tracker. Start free today—no credit card required.
      </p>
      <Link to="/register">
        <Button size="lg" className="bg-white text-blue-600 hover:bg-gray-100">
          Create Free Account <ArrowRight className="ml-2 h-4 w-4" />
        </Button>
      </Link>
    </CardContent>
  </Card>
);

const BlogArticle = () => {
  const { slug } = useParams();
  const article = articles[slug];

  if (!article) {
    return <Navigate to="/blog" replace />;
  }

  return (
    <div className="min-h-screen bg-white">
      {/* Navigation */}
      <nav className="bg-white border-b border-gray-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <Link to="/" className="flex items-center space-x-3">
              <img src={LOGO_URL} alt="Attendance Tracker" className="h-10 w-auto" />
              <span className="font-bold text-xl text-gray-900">Attendance Tracker</span>
            </Link>
            <div className="flex items-center space-x-4">
              <Link to="/login">
                <Button variant="ghost">Login</Button>
              </Link>
              <Link to="/register">
                <Button className="bg-blue-600 hover:bg-blue-700">Get Started Free</Button>
              </Link>
            </div>
          </div>
        </div>
      </nav>

      {/* Article */}
      <article className="py-12">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Back Link */}
          <Link to="/blog" className="inline-flex items-center text-blue-600 hover:text-blue-700 mb-8">
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back to Blog
          </Link>

          {/* Header */}
          <header className="mb-8">
            <Badge className="mb-4 bg-blue-100 text-blue-700">{article.category}</Badge>
            <h1 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              {article.title}
            </h1>
            <div className="flex items-center text-gray-500 text-sm space-x-4">
              <span className="flex items-center">
                <Calendar className="h-4 w-4 mr-1" />
                {article.date}
              </span>
              <span className="flex items-center">
                <Clock className="h-4 w-4 mr-1" />
                {article.readTime}
              </span>
            </div>
          </header>

          {/* Mid-article CTA */}
          <CallToAction />

          {/* Content */}
          <div 
            className="prose prose-lg max-w-none prose-headings:text-gray-900 prose-p:text-gray-600 prose-li:text-gray-600 prose-strong:text-gray-900"
            dangerouslySetInnerHTML={{ __html: article.content }}
          />

          {/* Bottom CTA */}
          <CallToAction />

          {/* Related Articles */}
          <div className="mt-12 pt-8 border-t border-gray-200">
            <h3 className="text-xl font-bold text-gray-900 mb-6">Continue Reading</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {Object.entries(articles)
                .filter(([key]) => key !== slug)
                .slice(0, 2)
                .map(([key, post]) => (
                  <Link key={key} to={`/blog/${key}`}>
                    <Card className="hover:shadow-md transition-shadow">
                      <CardContent className="p-4">
                        <Badge variant="outline" className="text-blue-600 border-blue-200 mb-2">
                          {post.category}
                        </Badge>
                        <h4 className="font-semibold text-gray-900 hover:text-blue-600">
                          {post.title}
                        </h4>
                      </CardContent>
                    </Card>
                  </Link>
                ))}
            </div>
          </div>
        </div>
      </article>

      {/* Footer */}
      <footer className="bg-gray-900 text-gray-400 py-8">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <Link to="/" className="text-white hover:text-gray-300">
            ← Back to Home
          </Link>
          <p className="mt-4">&copy; {new Date().getFullYear()} Attendance Tracker App. All rights reserved.</p>
        </div>
      </footer>
    </div>
  );
};

export default BlogArticle;
